import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { REGIONS } from 'constants/Regions';
import SessionStore from '../../stores/SessionStore';
import VerticalNav from '../v2/navs/VerticalNav';
import LayoutWithSidebar from '../v2/LayoutWithSidebar';

// Pages Import
import EDIResender from './tools/EDIResender';
import PODDownloader from './tools/PODDownloader';
import BulkAdjustments from './shipments/BulkAdjustments';
import ReferenceNumbersUpload from './shipments/ReferenceNumbersUpload';
import BulkShipmentCancellation from './tools/BulkShipmentCancellation';
import ExportDocuments from './tools/ExportDocuments';
import BulkShipmentRerate from './tools/BulkShipmentRerate';
import BulkEvaluateBillingStatus from './tools/BulkEvaluateBillingStatus';

const userDoesNotHaveShipperOrCarrierRole = () => {
  return !SessionStore.isShipper() && !SessionStore.isCarrier();
};

const getToolsData = () => {
  return [
    {
      title: 'BULK_BASE_COST_ADJUSTMENT',
      key: 'bulk-base-cost-adjustment',
      show: userDoesNotHaveShipperOrCarrierRole(),
    },
    {
      title: 'POD_DOWNLOADER',
      key: 'pod-downloader',
      show: SessionStore.isAdmin(),
    },
    {
      title: 'EDI_RESENDER',
      key: 'edi-resender',
      show:
        (SessionStore.isGodAdmin() ||
          SessionStore.hasIntegrationManagerRole()) &&
        REGION_CODE === REGIONS.US,
    },
    {
      title: 'REFERENCE_NUMBER_ADDITION',
      key: 'reference-number-addition',
      show: true,
    },
    {
      title: 'BULK_CANCEL_SHIPMENT',
      key: 'bulk-cancel-shipment',
      show: userDoesNotHaveShipperOrCarrierRole(),
    },
    {
      title: 'EXPORT_DOCUMENTS',
      key: 'export-documents',
      show: true,
    },
    {
      title: 'BULK_RERATE_SHIPMENT',
      key: 'bulk-rerate-shipment',
      show: userDoesNotHaveShipperOrCarrierRole(),
    },
    {
      title: 'Bulk Evaluate Shipments',
      key: 'bulk-override-billing-status',
      show: true,
    },
  ];
};

const ToolsNav = ({ title, pageName, currentPage, setCurrentPage, show }) => {
  const isActive = pageName === currentPage;
  if (!show) {
    return null;
  }
  return (
    <VerticalNav.NavItem
      key={pageName}
      id={pageName}
      onClick={() => setCurrentPage(pageName)}
      active={isActive}
      title={title}
    >
      {title}
    </VerticalNav.NavItem>
  );
};

const ToolsContent = ({ activePage }) => {
  switch (activePage) {
    case 'bulk-base-cost-adjustment':
      return <BulkAdjustments />;
    case 'edi-resender':
      return <EDIResender />;
    case 'pod-downloader':
      return <PODDownloader />;
    case 'reference-number-addition':
      return <ReferenceNumbersUpload />;
    case 'bulk-cancel-shipment':
      return <BulkShipmentCancellation />;
    case 'export-documents':
      return <ExportDocuments />;
    case 'bulk-rerate-shipment':
      return <BulkShipmentRerate />;
    case 'bulk-override-billing-status':
      return <BulkEvaluateBillingStatus />;
    default:
      return <BulkAdjustments />;
  }
};

const getBreadcrumbs = (t, pageSlug) => ({
  pages: [
    {
      name: t('TOOLS'),
      href: '/tools',
      current: true,
    },
    {
      name: t(pageSlug),
      current: true,
    },
  ],
});

const getSideBar = (t, page, setPage) => () => (
  <VerticalNav>
    {getToolsData().map(({ title, key: pageName, show }) => {
      return (
        <ToolsNav
          title={t(title)}
          pageName={pageName}
          show={show}
          currentPage={page}
          setCurrentPage={setPage}
        />
      );
    })}
  </VerticalNav>
);

const ToolsPage = () => {
  const [page, setPage] = useState('bulk-base-cost-adjustment');
  const pageSlug = getToolsData().filter((data) => data.key === page)[0]?.title;
  const { t } = useTranslation();

  return (
    <LayoutWithSidebar
      breadcrumbs={getBreadcrumbs(t, pageSlug)}
      sidebar={getSideBar(t, page, setPage)}
      className="py-10"
    >
      <div data-testid="toolsContent">
        <ToolsContent activePage={page} />
      </div>
    </LayoutWithSidebar>
  );
};

export default ToolsPage;
